import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Link from '@/components/utilities/link/link';
import { highlightTerm } from '@/libraries/formatters/highlight-term';
import styles from './combo-box.module.scss';

export interface ComboBoxOption {
  id: number;
  inputName: string;
  optionName: string;
  value: string | number;
}

export interface ComboBoxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** The direction of the dropdown */
  direction?: 'up' | 'down';
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** A text that is shown below the field and affects the styling */
  error?: string;
  /** A text that is shown below the field as help text */
  help?: string;
  /** The text that is shown above the field as a label */
  label?: string;
  /** An event that is triggered on blur */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** An event that is triggered on change */
  onChange?: (value: string | number) => void;
  /** An event that is triggered on focus */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** An array of the value options */
  options?: ComboBoxOption[];
  /** A text that is shown inside the field as a placeholder */
  placeholder?: string;
  /** Indicates whether the dropdown should be opened */
  shouldHideOptions?: boolean;
  /** The value of the field */
  value?: string;
}

const ComboBox = forwardRef<HTMLInputElement, ComboBoxProps>(
  (
    {
      direction = 'down',
      disabled,
      error,
      help,
      label,
      options = [],
      placeholder,
      value: propValue,
      shouldHideOptions = false,
      onChange,
      required,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(propValue || '');
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLAnchorElement | HTMLButtonElement | null)[]>(
      [],
    );

    const filteredOptions = options.filter(option =>
      option.optionName.toLowerCase().includes(inputValue.toLowerCase()),
    );

    const shouldShowDropdown =
      isOpen &&
      !shouldHideOptions &&
      inputValue.trim() !== '' &&
      filteredOptions.length > 0;

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsOpen(true);
      setInputValue(event.target.value);
      onChange?.(event.target.value as string);
    };

    const handleOptionClick = (option: ComboBoxOption) => {
      setIsOpen(false);
      setInputValue(option.inputName);
      onChange?.(option.id);

      if (!(document.activeElement instanceof HTMLElement)) return;

      document.activeElement.blur();
    };

    const handleMouseMove = () => {
      setHighlightedIndex(-1);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const totalItems = filteredOptions.length;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex(prev => {
          if (prev === -1 || prev === totalItems - 1) return 0;

          return prev + 1;
        });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex(prev => {
          if (prev === -1 || prev === 0) return totalItems - 1;

          return prev - 1;
        });
      } else if (
        event.key === 'Enter' &&
        highlightedIndex >= 0 &&
        highlightedIndex < filteredOptions.length
      ) {
        event.preventDefault();
        handleOptionClick(filteredOptions[highlightedIndex]);
      } else if (event.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      if (highlightedIndex < 0) return;

      optionRefs.current[highlightedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedIndex]);

    useEffect(() => {
      setHighlightedIndex(-1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    useEffect(() => {
      setInputValue(propValue || '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propValue]);

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('mousemove', handleMouseMove);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }, []);

    return (
      <div
        ref={containerRef}
        className={classNames(styles.component, {
          [styles.hasError]: error,
          [styles.hasLabel]: label,
          [styles.isDisabled]: disabled,
          [styles.up]: direction === 'up',
          [styles.down]: direction === 'down',
        })}
      >
        {label && (
          <Typography
            className={styles.label}
            tag="label"
            tagStyle="bodySmallBold"
          >
            {label}
            {required && <span className={styles.requiredMark}> *</span>}
          </Typography>
        )}

        <input
          ref={ref}
          className={styles.field}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          type="text"
          value={inputValue}
        />

        <AnimatePresence>
          {shouldShowDropdown && (
            <motion.div
              key="combo-dropdown"
              animate="show"
              className={styles.dropdown}
              exit="hide"
              initial="hide"
              transition={{ duration: 0.2 }}
              variants={{
                hide: { opacity: 0, y: direction === 'down' ? -8 : 8 },
                show: { opacity: 1, y: 0 },
              }}
            >
              <div className={styles.items}>
                {filteredOptions.map((option, index) => {
                  const highlightedOptionText = highlightTerm(
                    option.optionName,
                    inputValue,
                    'unmatched',
                    styles.highlight,
                  );

                  return (
                    <Link
                      key={index}
                      ref={el => {
                        optionRefs.current[index] = el;
                      }}
                      className={classNames(styles.option, {
                        [styles.highlighted]: index === highlightedIndex,
                      })}
                      onClick={() => handleOptionClick(option)}
                    >
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: highlightedOptionText,
                        }}
                        tagStyle="bodyLarge"
                      />
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {(error || help) && (
          <Typography className={styles.help} tagStyle="bodySmall">
            {error || help}
          </Typography>
        )}
      </div>
    );
  },
);

ComboBox.displayName = 'ComboBox';

export default ComboBox;
